import type { Ref } from 'vue';
import { ref } from 'vue';
import { NInputNumber } from 'naive-ui';
import { dictTypeRecord } from '@/constants/business';
import { YesOrNoOptions } from '@/constants/common';
import { getApiSelectConfig } from '@/constants/remote-option-config';
import { doUpdate } from '@/service/api/common/dict-data';
import { useAuth } from '@/hooks/business/auth';
import { useRemoteSelect } from '@/hooks/common/remote-select';
import type { TableColumn } from '@/components/advanced/basic-table/use-table';
import type { Api } from '@/typings/api';
import type { BasicForm } from '@/typings/basic-form';
import type { DictTypeEnum } from '@/enum';
import { RoleEnum } from '@/enum';

/**
 * 更新单条数据
 *
 * @param row
 * @param updateValue
 * @param loading
 */
async function updateRowData(updateValue: Api.Base.DictData, loading: Ref<boolean>) {
  try {
    loading.value = true;
    const result = await doUpdate(updateValue);
    if (result?.error === null) {
      window.$message?.success('更新成功');
    }
  } finally {
    loading.value = false;
  }
}
export const tableColumns = (): TableColumn<Api.Base.DictData>[] => {
  const { hasAuth, hasRole } = useAuth();
  return [
    // {
    //   key: 'type',
    //   title: '字典类型',
    //   dataType: 'number',
    //   enumRecord: dictTypeRecord,
    //   align: 'center',
    //   sorter: 'default',
    //   width: 150
    //   // search: {
    //   //   searchType: 'andIn',
    //   //   component: {
    //   //     type: 'NSelect',
    //   //     props: {
    //   //       options: dictTypeOptions,
    //   //       multiple: true
    //   //     }
    //   //   }
    //   // }
    // },
    // {
    //   key: 'parentDictData.label',
    //   title: '上级字典',
    //   dataType: 'object',
    //   width: 100,
    //   align: 'center',
    //   resizable: true,
    //   sorter: 'default'
    // },
    {
      key: 'label',
      title: '名称',
      dataType: 'string',
      align: 'center',
      width: 150,
      sorter: 'default',
      resizable: true,
      search: {
        searchType: 'andLike',
        component: {
          type: 'NInput'
        }
      }
    },
    {
      key: 'value',
      title: '值',
      dataType: 'string',
      align: 'center',
      width: 100,
      sorter: 'default',
      resizable: true,
      search: {
        searchType: 'andLike',
        component: {
          type: 'NInput'
        }
      }
    },
    {
      key: 'enable',
      title: '是否启用',
      dataType: 'boolean',
      align: 'center',
      width: 100,
      search: {
        searchType: 'andEq',
        component: {
          type: 'RadioGroup',
          props: {
            options: YesOrNoOptions
          }
        }
      }
    },
    {
      key: 'orderIndex',
      title: '排序',
      dataType: 'number',
      sorter: 'default',
      align: 'center',
      width: 120,
      render: row => {
        if (hasAuth('common:dictData:update')) {
          const loading = ref<boolean>(false);
          return (
            <NInputNumber
              value={row.orderIndex}
              loading={loading.value}
              size="small"
              onUpdate:value={val => {
                if (val !== null) {
                  row.orderIndex = val;
                  updateRowData(
                    {
                      id: row.id,
                      orderIndex: val
                    } as Api.Base.DictData,
                    loading
                  );
                }
              }}
            />
          );
        }
        return row.orderIndex;
      }
    },
    {
      key: 'remark',
      title: '备注',
      dataType: 'string',
      align: 'center',
      width: 100,
      ellipsis: {
        tooltip: true
      }
    },
    {
      key: 'customer.customerName',
      title: '所属客户',
      dataType: 'object',
      ifShow: () => hasRole(RoleEnum.SUPER_ADMIN),
      width: 150,
      align: 'center',
      resizable: true,
      sorter: 'default'
    },
    {
      key: 'createTime',
      title: '创建时间',
      dataType: 'datetime',
      align: 'center',
      sorter: 'default',
      width: 100
    },
    // {
    //   key: 'createUser.userName',
    //   title: '创建人',
    //   dataType: 'object',
    //   align: 'center',
    //   sorter: 'default',
    //   width: 100
    // },
    {
      key: 'updateTime',
      title: '更新时间',
      dataType: 'datetime',
      align: 'center',
      sorter: 'default',
      width: 100
    }
    // {
    //   key: 'updateUser.userName',
    //   title: '更新人',
    //   dataType: 'object',
    //   align: 'center',
    //   sorter: 'default',
    //   width: 100
    // }
  ];
};

export const dictDataTableColumns = tableColumns;

export const fieldSchemas: BasicForm.FieldSchemaCallbackFn<Api.Base.DictData> = params => {
  const { hasRole } = useAuth();
  return [
    // {
    //   key: 'type',
    //   title: '字典类型',
    //   dataType: 'string',
    //   required: true,
    //   component: {
    //     type: 'NSelect',
    //     props: {
    //       options: dictTypeOptions
    //     }
    //   }
    // },
    {
      key: 'parentDictData.label',
      requestRequiredColumns: ['type'],
      ifShow: ({ operation, data }) =>
        operation === 'view' && Boolean(data.type) && dictTypeRecord[data.type as DictTypeEnum].config?.isTree,
      title: '上级字典',
      dataType: 'string'
    },
    {
      key: 'parentDictData.id',
      requestRequiredColumns: ['type'],
      ifShow: ({ operation, data }) =>
        operation !== 'view' && Boolean(data.type) && dictTypeRecord[data.type as DictTypeEnum].config?.isTree,
      title: '上级字典',
      dataType: 'string',
      component: {
        type: 'NTreeSelect',
        register: ({ model, component, formData }) => {
          const filter: Api.Common.Filter[] = [
            {
              column: 'type',
              type: 'andEq',
              value: formData?.type
            }
          ];
          if (params?.otherParams?.customerId) {
            filter.push({
              column: 'customer.id',
              type: 'andEq',
              value: params?.otherParams?.customerId
            });
          }
          return useRemoteSelect({
            component,
            model,
            config: getApiSelectConfig('DictData', {
              filter
            }),
            props: {
              placeholder: '请选择上级字典'
            }
          });
        }

        // type: 'DictSelect',
        // props: ({ data }) => {
        //   return {
        //     dictType: data.type,
        //     placeholder: '请选择上级字典'
        //   };
        // }
      }
    },
    {
      key: 'label',
      title: '名称',
      dataType: 'string',
      required: true,
      component: {
        type: 'NInput',
        props: {
          maxlength: 50
        }
      },
      set: ({ value, data }) => {
        if (value) {
          data.value = value;
        }
        return value;
      }
    },
    {
      key: 'value',
      title: '值',
      dataType: 'string',
      required: true,
      component: {
        type: 'NInput',
        props: {
          maxlength: 100
        }
      }
    },
    {
      key: 'orderIndex',
      title: '排序',
      dataType: 'number',
      defaultValue: 0,
      required: true,
      component: {
        type: 'NInputNumber'
      }
    },
    {
      key: 'enable',
      title: '启用',
      dataType: 'boolean',
      defaultValue: true,
      required: true,
      ruleType: 'boolean',
      component: {
        type: 'NSwitch'
      }
    },
    {
      key: 'customer.id',
      title: '所属客户',
      dataType: 'string',
      ifShow: ({ operation, disabled }) => operation !== 'view' && !disabled && hasRole(RoleEnum.SUPER_ADMIN),
      component: {
        type: 'NSelect',
        register: ({ model, component }) =>
          useRemoteSelect({
            component,
            model,
            config: getApiSelectConfig('CustomerInfo'),
            props: {
              placeholder: '请选择所属客户'
            }
          })
      }
    },
    {
      key: 'customer.customerName',
      title: '所属客户',
      dataType: 'string',
      ifShow: ({ operation, disabled }) => (operation === 'view' || Boolean(disabled)) && hasRole(RoleEnum.SUPER_ADMIN)
    },
    {
      key: 'remark',
      title: '备注',
      dataType: 'string',
      component: {
        type: 'NInput',
        props: {
          maxlength: 255
        }
      }
    },
    {
      key: 'tempId',
      title: '附件',
      dataType: 'string',
      tempField: true,
      ruleType: 'string',
      colSpan: 24,
      component: {
        type: 'BasicUpload',
        props: ({ data, operation }) => {
          return {
            entityName: 'DictData',
            typeCodes: ['dict_logo', 'dict_img'],
            dataId: operation === 'add' ? undefined : data?.id
          };
        }
      }
    }
  ];
};
export const dictDataFieldSchemas = fieldSchemas;
